import React, { useEffect, useState } from 'react';
import {
    Card,
    FormField,
    useRouteParams,
    useQuery,
    useMutation,
} from '@vendure/admin-ui/react';
import { gql } from 'graphql-tag';
import { useInjector } from '@vendure/admin-ui/react';
import { NotificationService, I18nService } from '@vendure/admin-ui/core';

type Channel = {
    code: string;
    id: string;
    token: string;
};

const GET_CHANNELS = gql`
    query GetChannels {
        channels {
            items {
                id
                code
                token
            }
        }
    }
`;

const GET_CUSTOMER_CHANNELS = gql`
    query GetChannels($args: GetCustomerChannelsInput!) {
        getCustomerChannels(args: $args) {
            code
            id
        }
    }
`;

const SAVE_SELECTED_CHANNELS = gql`
    mutation SetCustomerChannels($args: SetCustomerChannelsInput!) {
        setCustomerChannels(args: $args) {
            status
        }
    }
`;

export function ChannelSelector() {
    const { params } = useRouteParams();
    const notificationService = useInjector(NotificationService);
    const i18nService = useInjector(I18nService);
    const customerId = params?.id;
    /*  querys */
    const { data, loading, error } = useQuery<{
        channels?: {
            items: Channel[];
        };
    }>(GET_CHANNELS, {}, { refetchOnChannelChange: true });
    const channels = data?.channels?.items ?? [];
    const {
        data: currentChannelsData,
        loading: loadingCurrentChannels,
        error: errorCurrentChannels,
    } = useQuery<{
        getCustomerChannels?: Channel[];
    }>(
        GET_CUSTOMER_CHANNELS,
        { args: { customerId } },
        { refetchOnChannelChange: true }
    );
    const currentChannels = currentChannelsData?.getCustomerChannels ?? [];
    /* mutation */
    const [
        saveSelectedChannels,
        {
            data: saveSelectedChannelsData,
            loading: loadingSaveSelectedChannels,
            error: errorSaveSelectedChannels,
        },
    ] = useMutation(SAVE_SELECTED_CHANNELS);

    const handleClick = () => {
        saveSelectedChannels({
            args: {
                customerId,
                selectedChannelsIds,
            },
        })
            .then((result) => {
                notificationService.success(
                    i18nService.translate('custom.save-success')
                );
            })
            .catch((error) => {
                notificationService.error(
                    i18nService.translate('custom.save-error')
                );
            });
    };

    const [selectedChannelsIds, setSelectedChannelsIds] = useState<string[]>(
        []
    );

    const toggleChannel = (id: string) => {
        setSelectedChannelsIds((prev) =>
            prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
        );
    };

    useEffect(() => {
        const filtered = currentChannels
            .filter((c) => c.code !== '__default_channel__')
            .map((c) => c.id);
        setSelectedChannelsIds(filtered);
    }, [currentChannels]);

    if (loading) return <div>{i18nService.translate('custom.loading')}</div>;
    if (error)
        return (
            <div>
                {i18nService.translate('custom.error')} {error}
            </div>
        );

    return (
        <Card>
            <div className="title-row">
                <span className="title">
                    {i18nService.translate('common.channels')}
                </span>
            </div>
            <div className="form-grid" style={{ paddingBottom: '30px' }}>
                <FormField>
                    <div
                        style={{
                            maxHeight: '300px',
                            overflowY: 'scroll',
                            width: '100%',
                        }}
                    >
                        <div className="" style={{ maxHeight: '200px' }}>
                            {channels
                                .filter((c) => c.code !== '__default_channel__')
                                .sort((a, b) => {
                                    if (a.code < b.code) return -1;
                                    return 1;
                                })
                                .map((channel) => (
                                    <div
                                        key={channel.id}
                                        className=""
                                        onClick={() =>
                                            toggleChannel(channel.id)
                                        }
                                        style={{
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedChannelsIds.includes(
                                                channel.id
                                            )}
                                            readOnly
                                            className="mr-2"
                                        />
                                        {channel.code}
                                    </div>
                                ))}
                        </div>
                    </div>
                </FormField>
            </div>
            <button
                className="button primary"
                disabled={loadingSaveSelectedChannels}
                onClick={() => handleClick()}
            >
                {' '}
                {loadingSaveSelectedChannels
                    ? i18nService.translate('custom.loading')
                    : i18nService.translate('custom.save')}
            </button>
        </Card>
    );
}
